// Site navigation map + landing page copy for TraceElements
// Nav sections: about, solutions (merged: solutions + industries), work, insights, services, careers
// The "industries" object below is kept for /industries route resolution but hidden from the nav.

export const siteSections = {
  about: {
    slug: "about",
    label: "About Us",
    landing: {
      kicker: "// WHO WE ARE",
      title: "Two founders. One execution layer.",
      tagline: "The engineering-first firm redefining what a consultancy delivers.",
      intro:
        "TraceElements is a boutique operating firm built by engineers and operators — not sales cycles. Explore how we structure the organization, invest in our people, and hold ourselves accountable to the standards we sell.",
    },
    items: [
      { slug: "the-engine", label: "The Engine", href: "/the-engine" },
      { slug: "solutions", label: "Solutions & Industries", href: "/solutions" },
      { slug: "who-we-are", label: "Who We Are" },
      { slug: "leadership", label: "Leadership" },
      { slug: "mission-and-values", label: "Mission and Values" },
      { slug: "teams", label: "Teams" },
      { slug: "life-at-traceelements", label: "Life at TraceElements" },
      { slug: "inclusion", label: "Inclusion" },
      { slug: "corporate-responsibility", label: "Corporate Responsibility" },
      { slug: "corporate-initiatives", label: "Corporate Initiatives" },
      { slug: "accolades", label: "Accolades" },
      { slug: "alumni-network", label: "Alumni Network" },
      { slug: "locations", label: "Locations" },
      { slug: "newsroom", label: "Newsroom" },
      { slug: "webinars", label: "Webinars" },
    ],
  },
  solutions: {
    slug: "solutions",
    label: "Solutions & Industries",
    landing: {
      kicker: "// OPERATIONAL CAPABILITIES",
      title: "End-to-end solutions engineered for scale.",
      tagline: "Every capability shipped from the same architects who scoped it.",
      intro:
        "From AI and cloud to regulatory compliance and transaction services — every TraceElements solution is engineered, project-managed, and audited under one accountable roof.",
    },
    items: [
      // ── Solutions ──
      { slug: "artificial-intelligence", label: "Artificial Intelligence", group: "Solutions" },
      { slug: "cloud", label: "Cloud", group: "Solutions" },
      { slug: "customer-experience", label: "Customer Experience", group: "Solutions" },
      { slug: "cybersecurity", label: "Cybersecurity", group: "Solutions" },
      { slug: "data-and-analytics", label: "Data and Analytics", group: "Solutions" },
      { slug: "data-privacy", label: "Data Privacy", group: "Solutions" },
      { slug: "digital-transformation", label: "Digital Transformation", group: "Solutions" },
      { slug: "ecosystem-partners", label: "Ecosystem Partners", group: "Solutions" },
      { slug: "finance-transformation", label: "Finance Transformation", group: "Solutions" },
      { slug: "internal-audit", label: "Internal Audit", group: "Solutions" },
      { slug: "legal-consulting", label: "Legal Consulting", group: "Solutions" },
      { slug: "managed-solutions", label: "Managed Solutions", group: "Solutions" },
      { slug: "operations", label: "Operations", group: "Solutions" },
      { slug: "people-and-change", label: "People & Change", group: "Solutions" },
      { slug: "quantum-computing", label: "Quantum Computing", group: "Solutions" },
      { slug: "regulatory-compliance", label: "Regulatory Compliance", group: "Solutions" },
      { slug: "risk-management", label: "Risk Management", group: "Solutions" },
      { slug: "sarbanes-oxley", label: "Sarbanes-Oxley", group: "Solutions" },
      { slug: "sustainability", label: "Sustainability", group: "Solutions" },
      { slug: "technology", label: "Technology", group: "Solutions" },
      { slug: "transaction-services", label: "Transaction Services", group: "Solutions" },
      // ── Industries ──
      { slug: "aerospace-defense", label: "Aerospace & Defense", href: "/industries/aerospace-defense", group: "Industries" },
      { slug: "asset-wealth-management", label: "Asset & Wealth Management", href: "/industries/asset-wealth-management", group: "Industries" },
      { slug: "banking-capital-markets", label: "Banking & Capital Markets", href: "/industries/banking-capital-markets", group: "Industries" },
      { slug: "consumer-goods", label: "Consumer Goods", href: "/industries/consumer-goods", group: "Industries" },
      { slug: "construction-infrastructure", label: "Construction & Infrastructure", href: "/industries/construction-infrastructure", group: "Industries" },
      { slug: "digital-assets-blockchain", label: "Digital Assets & Blockchain", href: "/industries/digital-assets-blockchain", group: "Industries" },
      { slug: "education", label: "Education", href: "/industries/education", group: "Industries" },
      { slug: "government", label: "Government", href: "/industries/government", group: "Industries" },
      { slug: "healthcare", label: "Healthcare", href: "/industries/healthcare", group: "Industries" },
      { slug: "hospitality-leisure-travel", label: "Hospitality, Leisure & Travel", href: "/industries/hospitality-leisure-travel", group: "Industries" },
      { slug: "insurance", label: "Insurance", href: "/industries/insurance", group: "Industries" },
      { slug: "manufacturing-distribution", label: "Manufacturing & Distribution", href: "/industries/manufacturing-distribution", group: "Industries" },
      { slug: "mortgage-consumer-lending", label: "Mortgage & Consumer Lending", href: "/industries/mortgage-consumer-lending", group: "Industries" },
      { slug: "non-profit", label: "Non-Profit", href: "/industries/non-profit", group: "Industries" },
      { slug: "oil-gas-resources", label: "Oil, Gas & Resources", href: "/industries/oil-gas-resources", group: "Industries" },
      { slug: "payments", label: "Payments", href: "/industries/payments", group: "Industries" },
      { slug: "pharma-life-sciences", label: "Pharma & Life Sciences", href: "/industries/pharma-life-sciences", group: "Industries" },
      { slug: "power-utilities-renewables", label: "Power, Utilities & Renewables", href: "/industries/power-utilities-renewables", group: "Industries" },
      { slug: "private-equity", label: "Private Equity", href: "/industries/private-equity", group: "Industries" },
      { slug: "retail", label: "Retail", href: "/industries/retail", group: "Industries" },
      { slug: "technology-media-telecom", label: "Technology, Media & Telecom", href: "/industries/technology-media-telecom", group: "Industries" },
      { slug: "transportation-logistics", label: "Transportation & Logistics", href: "/industries/transportation-logistics", group: "Industries" },
    ],
  },
  industries: {
    slug: "industries",
    label: "Industries",
    landing: {
      kicker: "// SECTOR VECTORS",
      title: "Deep domain fluency across 22 industries.",
      tagline: "Regulatory nuance, market pressure, and operating rhythm — encoded.",
      intro:
        "We staff every engagement with practitioners who have lived the market they're advising. No generic playbooks.",
    },
    items: [
      { slug: "aerospace-defense", label: "Aerospace & Defense" },
      { slug: "asset-wealth-management", label: "Asset & Wealth Management" },
      { slug: "banking-capital-markets", label: "Banking & Capital Markets" },
      { slug: "consumer-goods", label: "Consumer Goods" },
      { slug: "construction-infrastructure", label: "Construction & Infrastructure" },
      { slug: "digital-assets-blockchain", label: "Digital Assets & Blockchain" },
      { slug: "education", label: "Education" },
      { slug: "government", label: "Government" },
      { slug: "healthcare", label: "Healthcare" },
      { slug: "hospitality-leisure-travel", label: "Hospitality, Leisure & Travel" },
      { slug: "insurance", label: "Insurance" },
      { slug: "manufacturing-distribution", label: "Manufacturing & Distribution" },
      { slug: "mortgage-consumer-lending", label: "Mortgage & Consumer Lending" },
      { slug: "non-profit", label: "Non-Profit" },
      { slug: "oil-gas-resources", label: "Oil, Gas & Resources" },
      { slug: "payments", label: "Payments" },
      { slug: "pharma-life-sciences", label: "Pharma & Life Sciences" },
      { slug: "power-utilities-renewables", label: "Power, Utilities & Renewables" },
      { slug: "private-equity", label: "Private Equity" },
      { slug: "retail", label: "Retail" },
      { slug: "technology-media-telecom", label: "Technology, Media & Telecom" },
      { slug: "transportation-logistics", label: "Transportation & Logistics" },
    ],
  },
  insights: {
    slug: "insights",
    label: "Insights",
    landing: {
      kicker: "// FIELD.NOTES",
      title: "Signal, not noise.",
      tagline: "Perspectives from the desks doing the actual work.",
      intro: "Frameworks, teardowns, and post-mortems from active engagements. No thought-leadership fluff.",
    },
    items: [
      { slug: "artificial-intelligence", label: "Artificial Intelligence (AI)" },
      { slug: "audit-risk-management", label: "Audit & Risk Management" },
      { slug: "board-c-suite-leadership", label: "Board and C-Suite Leadership" },
      { slug: "cybersecurity", label: "Cybersecurity" },
      { slug: "digital-experience", label: "Digital Experience" },
      { slug: "finance-operations", label: "Finance & Operations" },
      { slug: "financial-services-industry", label: "Financial Services Industry" },
      { slug: "regulation", label: "Regulation" },
      { slug: "sustainability", label: "Sustainability" },
      { slug: "technology-industry", label: "Technology Industry" },
      { slug: "transformation-innovation", label: "Transformation and Innovation" },
    ],
  },
  careers: {
    slug: "careers",
    label: "Careers",
    landing: {
      kicker: "// JOIN.THE.ENGINE",
      title: "Build the work you'd stay for.",
      tagline: "Elite operators. Zero politics.",
      intro:
        "We hire engineers and operators who need momentum. Compensation is transparent, ownership is real, and the work ships.",
    },
    items: [
      { slug: "careers-home", label: "Careers Home" },
      { slug: "experienced-hires", label: "Experienced Hires" },
      { slug: "students-graduates", label: "Students & Graduates" },
      { slug: "meet-our-people", label: "Meet Our People" },
      { slug: "careers-blog", label: "TraceElements Careers Blog" },
      { slug: "making-an-impact", label: "Making An Impact" },
      { slug: "your-well-being", label: "Your Well-Being" },
    ],
  },
};

// Ordered list of top-level nav sections for header
export const primaryNav = ["about", "work", "insights", "services", "careers"];

// Services (monthly retainers + private AI deployment)
siteSections.services = {
  slug: "services",
  label: "Services",
  overviewHref: "/services",
  landing: {
    kicker: "// SERVICES",
    title: "Monthly retainers + private AI deployments.",
    tagline: "Two engines. One accountable firm.",
    intro:
      "From growth marketing retainers to on-prem LLM deployment for regulated enterprises — TraceElements ships both under the same senior operating standard.",
  },
  items: [
    { slug: "marketing", label: "Growth Marketing · retainers", href: "/services/marketing" },
    { slug: "private-ai", label: "Private AI Deployment · on-prem", href: "/services/private-ai" },
    { slug: "audits", label: "AI Risk & Compliance Audits", href: "/services/audits" },
    { slug: "web-engineering", label: "Web Engineering & Ecosystem · 40% off", href: "/services/web-engineering" },
    { slug: "launch", label: "Zero to Launch · business builder", href: "/services/launch" },
    { slug: "saas", label: "SaaS Startup Blueprint · MVP → PMF", href: "/services/saas" },
    { slug: "video", label: "AI Video Production · cinematic kits", href: "/services/video" },
    { slug: "os", label: "TraceElements OS · managed CRM platform", href: "/services/os" },
    { slug: "geo-cat", label: "GEO Dominance · AI citation engine", href: "/geo" },
    { slug: "overview", label: "All Services Overview", href: "/services" },
  ],
};

// GEO Dominance — standalone product line, direct nav link (no dropdown)
siteSections.geo = {
  slug: "geo",
  label: "GEO",
  directLink: true,
  overviewHref: "/geo",
  landing: {
    kicker: "// GEO.DOMINANCE",
    title: "The 90-Day AI Citation Engine.",
    tagline: "Get cited across ChatGPT · Claude · Gemini · Perplexity.",
    intro: "Strategic Generative Engine Optimization for premium wellness, longevity, and D2C brands. Become the answer AI models give — not the competitor.",
  },
  items: [
    { slug: "authority", label: "Tier 1 · Category Authority Sprint", href: "/services/geo-authority" },
    { slug: "enterprise", label: "Tier 2 · Enterprise AI Dominance", href: "/services/geo-enterprise" },
    { slug: "shield", label: "Recurring · AI Defense & Citation Shield", href: "/services/geo-shield" },
  ],
};

// Our Work — direct-link nav entry showcasing shipped projects
siteSections.work = {
  slug: "work",
  label: "Our Work",
  directLink: true,
  overviewHref: "/work",
  landing: {
    kicker: "// WORK.CATALOG",
    title: "The work, not the pitch.",
    tagline: "Videos, sites, and SaaS apps we've shipped.",
    intro: "A running catalog of what we've delivered — AI video vaults, marketing sites, production SaaS apps.",
  },
  items: [
    { slug: "videos", label: "AI Video Reels", href: "/work" },
    { slug: "sites", label: "Websites & Landing Pages", href: "/work" },
    { slug: "apps", label: "SaaS Apps & Portals", href: "/work" },
  ],
};
